import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { SessionService } from '../../_services/session/session.service';
import IUser from '../../_interfaces/IUser';
import { IAppNotification } from '../../_interfaces/IReminder';
import { NotificationsService } from '../../_services/notifications/notifications.service';

// PrimeNG
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { SupportReportDialogComponent } from '../support/support-report-dialog.component';
import { ISupportTicket } from '../../_interfaces/ISupportTicket';
import { MessageService } from 'primeng/api';

@Component({
  standalone: true,
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MenuModule,
    TooltipModule,
    RippleModule,
    SupportReportDialogComponent,
  ],
})
export class TopbarComponent implements OnInit, OnDestroy {
  @Input() pageTitle = '';
  @Input() pageIcon = '';
  @Input() isSidebarOpen = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  public user: IUser | null = null;
  public context: any = null;
  public languageItems: MenuItem[] = [];
  public currentLang = 'EN';
  public activeMode: 'professional' | 'patient' = 'professional';
  public notifications: IAppNotification[] = [];
  public unreadCount = 0;
  public isNotificationsOpen = false;
  public showSupportReport = false;

  private subscriptions: Subscription[] = [];
  private notificationPoll: any = null;

  constructor(
    private session: SessionService,
    private translateService: TranslateService,
    private notificationsService: NotificationsService,
    private router: Router,
    private messages: MessageService,
  ) {}

  ngOnInit() {
    // Get initial user from localStorage
    this.user = this.session.getUser();
    this.context = this.session.getContext();
    this.activeMode = this.session.getActiveMode();

    // Subscribe to user changes
    const userSub = this.session.getUser$().subscribe((user) => {
      this.user = user;
    });
    this.subscriptions.push(userSub);

    const contextSub = this.session.getContext$().subscribe((context) => {
      this.context = context;
      this.activeMode =
        context?.session?.active_mode === 'patient' ? 'patient' : 'professional';
      this.loadNotificationSummary();
    });
    this.subscriptions.push(contextSub);

    // Language setup
    this.currentLang = this.translateService.currentLang?.toUpperCase() || 'EN';
    this.setLanguages();

    // Update on language change
    const langSub = this.translateService.onLangChange.subscribe(() => {
      this.currentLang = this.translateService.currentLang.toUpperCase();
      this.setLanguages();
    });
    this.subscriptions.push(langSub);

    this.loadNotificationSummary();
    this.notificationPoll = setInterval(() => this.loadNotificationSummary(), 60000);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.notificationPoll) {
      clearInterval(this.notificationPoll);
    }
  }

  private setLanguages() {
    this.languageItems = [
      {
        label: 'Español',
        icon: 'pi pi-flag',
        command: () => this.session.setLanguage('es'),
      },
      {
        label: 'English',
        icon: 'pi pi-flag',
        command: () => this.session.setLanguage('en'),
      },
      {
        label: 'Català',
        icon: 'pi pi-flag',
        command: () => this.session.setLanguage('ca'),
      },
    ];
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  getLangFlag(): string {
    const flags: Record<string, string> = {
      ES: '🇪🇸',
      EN: '🇬🇧',
      CA: '🇪🇸',
    };
    return flags[this.currentLang] || '🌐';
  }

  toggleNotifications() {
    this.isNotificationsOpen = !this.isNotificationsOpen;
    if (!this.isNotificationsOpen) return;
    this.loadNotifications(true);
  }

  supportCreated(ticket: ISupportTicket) {
    this.messages.add({ severity: 'success', summary: this.translateService.instant('SUPPORT.CREATED') });
    this.router.navigate(['/support', ticket.id]);
  }

  openNotification(notification: IAppNotification) {
    this.isNotificationsOpen = false;
    if (notification.link_url) {
      this.router.navigateByUrl(notification.link_url);
    }
  }

  private loadNotificationSummary() {
    const customerId = this.activeCustomerId;
    if (!customerId || this.isPatientMode) {
      this.unreadCount = 0;
      this.notifications = [];
      return;
    }
    this.notificationsService.getSummary(customerId).subscribe({
      next: (summary) => {
        this.unreadCount = Number(summary.unreadCount || 0);
        this.notifications = summary.latestNotifications || [];
      },
      error: () => {
        this.unreadCount = 0;
      },
    });
  }

  private loadNotifications(markAsRead: boolean) {
    const customerId = this.activeCustomerId;
    if (!customerId) return;
    this.notificationsService.getNotifications(customerId, 0, 10).subscribe({
      next: (response) => {
        this.notifications = response.data || [];
        if (markAsRead && this.unreadCount > 0) {
          this.notificationsService.markAllAsRead(customerId).subscribe({
            next: () => {
              this.unreadCount = 0;
              this.notifications = this.notifications.map((item) => ({
                ...item,
                is_read: true,
              }));
            },
          });
        }
      },
    });
  }

  get activeCustomerName(): string {
    return this.context?.activeCustomer?.name || '-';
  }

  get activeCustomerId(): number {
    return Number(
      this.context?.activeCustomer?.id ||
      this.session.getCustomer()?.id ||
      0,
    );
  }

  get activeSiteName(): string {
    return this.context?.activeSite?.name || '-';
  }

  get isActingSuperadmin(): boolean {
    return !!this.context?.session?.is_acting;
  }

  get isPatientMode(): boolean {
    return this.activeMode === 'patient';
  }

  get profileRoute(): string {
    return this.isPatientMode ? '/patient/profile' : '/profile';
  }
}
